import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import CookieConsent, { Cookies } from "react-cookie-consent";
import Sidebar from './components/layout/Sidebar';
import Toolbar from './components/layout/Toolbar';
import { DesignCanvas } from './components/canvas/DesignCanvas';
import CanvasToolbar from './components/canvas/CanvasToolbar';
import ChatPanel from './components/layout/ChatPanel';
import { CodePreview } from './components/canvas/CodePreview';
import { ProgressBar } from './components/canvas/ProgressBar';
import AuthPage from './components/auth/AuthPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import GeminiService from './services/geminiService';
import HelpPage from './components/help/HelpPage';
import ProfilePage from './pages/ProfilePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

// Protected route component (Keep for potential future use or specific routes)
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!loading && !user) {
      // Only redirect if trying to access a *truly* protected route
      // For now, we remove the default redirect from here
      // navigate('/auth'); 
    }
  }, [user, loading, navigate]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg-primary">
        <div className="w-12 h-12 border-4 border-blue-600 dark:border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  // Return children if user exists, otherwise null or a redirect specific to the protected resource
  return user ? children : null; 
}

// MainLayout component that includes the sidebar and main content area
function MainLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  return (
    <div className="flex flex-col h-screen">
      <div className="flex-1 flex overflow-hidden bg-gray-50 dark:bg-dark-bg-primary relative">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <div className="flex-1 overflow-hidden">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

// Function to load and initialize Google Analytics
const GA_MEASUREMENT_ID = "G-RV1TR3VDHF"; // Store your Measurement ID

const loadGoogleAnalytics = () => {
  // Check if script already exists
  if (document.getElementById('google-analytics-script')) {
    console.log("Google Analytics script already loaded.");
    return;
  }
  
  const script = document.createElement('script');
  script.id = 'google-analytics-script';
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;

  script.onload = () => {
    console.log("Google Analytics script loaded successfully.");
    window.dataLayer = window.dataLayer || [];
    // Define gtag function if it doesn't exist
    if (typeof window.gtag !== 'function') {
      window.gtag = function(){
        window.dataLayer.push(arguments);
      }
    }
    // Set default consent state BEFORE gtag('js')
    window.gtag('consent', 'default', {
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'analytics_storage': 'denied'
    });

    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID);
    console.log("Google Analytics configured.");
    // Optionally, send a pageview event after configuration
    // window.gtag('event', 'page_view', { page_path: window.location.pathname });
  };

  script.onerror = () => {
    console.error("Failed to load Google Analytics script.");
  };

  document.head.appendChild(script);
  console.log("Attempting to load Google Analytics script...");
};

// Main App UI Component
function AppUI() {
  const { user } = useAuth();
  
  // State for analytics consent - initializes based on cookie
  const [analyticsConsentGiven, setAnalyticsConsentGiven] = useState(Cookies.get("CookieConsent") === "true");

  // Load GA script on mount if consent was already given
  useEffect(() => {
    if (analyticsConsentGiven) {
      console.log("Consent already given, loading GA on mount.");
      loadGoogleAnalytics();
    }
  }, [analyticsConsentGiven]); // Run when consent state changes

  // State variables 
  const [messages, setMessages] = useState([
    { role: 'system', content: 'What should we forge?' }
  ]);
  const [userInput, setUserInput] = useState('');
  const [currentHTML, setCurrentHTML] = useState(''); // State for the generated HTML
  const [annotations, setAnnotations] = useState([]); // State for drawing annotations
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [selectedDrawings, setSelectedDrawings] = useState([]); // New state for tracking which drawings are selected
  const [showCode, setShowCode] = useState(false); // State to toggle between design and code view
  const [isGenerating, setIsGenerating] = useState(false); // State for tracking when AI is generating
  // eslint-disable-next-line no-unused-vars
  const [isBackendReady, setIsBackendReady] = useState(false); // Track backend/Gemini status
  const [currentDeviceType, setCurrentDeviceType] = useState('web'); // Track the current device type
  const [activeTool, setActiveTool] = useState('select');
  const designCanvasRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [contextSize, setContextSize] = useState(0); // Track context size
  const [showContextWarning, setShowContextWarning] = useState(false); // Warning state
  
  // Version history tracking
  const [versionHistory, setVersionHistory] = useState([]); // Store version history
  const MAX_HISTORY_ITEMS = 10; // Maximum number of history items to store
  
  // Progress tracking
  const [progress, setProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const progressInterval = useRef(null);
  
  // Function to start progress simulation
  const startProgressSimulation = () => {
    // Reset progress
    setProgress(0);
    setTimeRemaining(30); // Initial estimate: 30 seconds
    
    // Clear any existing interval
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }
    
    // Create a new interval that updates progress
    progressInterval.current = setInterval(() => {
      setProgress(prevProgress => {
        // Calculate new progress - slower at the beginning, faster towards the end
        const newProgress = prevProgress < 70 
          ? prevProgress + 1 
          : prevProgress + 0.5;
        
        // Update time remaining based on progress
        if (newProgress <= 100) {
          const remainingSeconds = Math.max(0, Math.ceil(30 * (100 - newProgress) / 100));
          setTimeRemaining(remainingSeconds);
        }
        
        // Cap progress at 95% until we get the actual response
        return Math.min(newProgress, 95);
      });
    }, 300); // Update every 300ms
  };
  
  // Function to complete progress and clear interval
  const completeProgress = () => {
    setProgress(100);
    setTimeRemaining(0);
    
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
  };

  // Function to update context size and check for warnings
  const updateContextSize = useCallback((newMessages) => {
    // Calculate approximate token count (rough estimation)
    const contextText = newMessages.map(m => m.content).join(' ');
    const htmlTokens = currentHTML ? currentHTML.length / 4 : 0; // Rough HTML token estimation
    const totalTokens = contextText.length / 4 + htmlTokens; // Approximate 4 chars per token
    
    setContextSize(totalTokens);
    
    // Show warning if context is getting large (15000 tokens is ~75% of context window)
    const showWarning = totalTokens > 15000;
    setShowContextWarning(showWarning);
    
    return totalTokens;
  }, [currentHTML]); // Add currentHTML as dependency

  // Function to add a version to history
  const addVersionToHistory = (html, description = null) => {
    if (!html) return; // Don't add empty HTML
    
    const newVersion = {
      id: Date.now(), // Unique identifier
      html: html,
      timestamp: new Date().toISOString(),
      description: description,
      deviceType: currentDeviceType
    };
    
    setVersionHistory(prevHistory => {
      // Add new version at the beginning
      const newHistory = [newVersion, ...prevHistory];
      
      // Limit to MAX_HISTORY_ITEMS items
      if (newHistory.length > MAX_HISTORY_ITEMS) {
        return newHistory.slice(0, MAX_HISTORY_ITEMS);
      }
      
      return newHistory;
    });
  };
  
  // Function to load a version from history
  const loadVersionFromHistory = (versionId) => {
    const version = versionHistory.find(v => v.id === versionId);
    if (version) {
      setCurrentHTML(version.html);
      if (version.deviceType) {
        setCurrentDeviceType(version.deviceType);
      }
      
      // Add a message about loading from history
      setMessages(prevMessages => [
        ...prevMessages,
        { role: 'system', content: `Loaded design version from ${new Date(version.timestamp).toLocaleTimeString()}` }
      ]);
    }
  };
  
  // Function to clear version history
  const clearVersionHistory = () => {
    setVersionHistory([]);
  };

  // --- NEW Handler for Device Type Change ---
  const handleDeviceTypeChange = (newDeviceType) => {
    if (newDeviceType !== currentDeviceType) {
        console.log(`Changing device type state to: ${newDeviceType}`);
        setCurrentDeviceType(newDeviceType);
        // Add a system message indicating the change
        setMessages(prevMessages => [
            ...prevMessages,
            { role: 'system', content: `Changed device view to ${newDeviceType}` }
        ]);
        // Optionally, you might want to clear annotations or reset zoom/pan here too
        setAnnotations([]);
        setSelectedDrawings([]);
        // Reset zoom/pan is handled inside DesignCanvas useEffect
    }
  };

  // --- Event Handlers ---

  // Handle general chat messages (basic queries or irrelevant queries)
  const handleChatMessage = async (text, deviceType = 'web', designStyle = 'creative') => {
    if (!text.trim() || isGenerating) return;
    
    const newUserMessage = {
      role: 'user', 
      content: text,
      deviceType
    };
    
    const newMessages = [
      ...messages,
      newUserMessage
    ];
    
    // Update context size and check if we're approaching limits
    const estimatedTokens = updateContextSize(newMessages);
    
    setMessages(newMessages);
    setUserInput('');
    setIsGenerating(true);
    
    try {
      // Show loading message
      const loadingMessages = [...newMessages, { role: 'system', content: 'Thinking...', loading: true }];
      setMessages(loadingMessages);
      
      // Check message intent classification
      // Keywords that specifically indicate a NEW design creation request
      const newDesignKeywords = [
        'create a new', 
        'generate a new', 
        'design a new', 
        'start over', 
        'start from scratch',
        'make a new',
        'create from scratch'
      ];
      
      // Check if this is explicitly a NEW design request
      const isExplicitNewDesignRequest = newDesignKeywords.some(keyword => 
        text.toLowerCase().includes(keyword)
      );
      
      // Handle ambiguous cases with confirmation when we have existing HTML
      if (currentHTML && !isExplicitNewDesignRequest) {
        // First, check if message looks like an update to existing design
        const lastSystemMessage = messages.filter(m => m.role === 'system').pop();
        const isConfirmationResponse = text.toLowerCase().match(/^(yes|no|update|new)$/);
        const isUserConfirmingUpdate = isConfirmationResponse && (text.toLowerCase() === 'yes' || text.toLowerCase() === 'update');
        const isUserRequestingNew = isConfirmationResponse && (text.toLowerCase() === 'no' || text.toLowerCase() === 'new');
        
        // If the context is too large, warn the user
        if (estimatedTokens > 20000) {
          setMessages([
            ...newMessages,
            { 
              role: 'system', 
              content: `The context window is getting too full to process more modifications. You might want to reset the design to start fresh if you encounter issues.`, 
              warning: true 
            }
          ]);
        }
        
        // If previous message was asking for confirmation and user confirmed
        if (lastSystemMessage && 
            lastSystemMessage.content.includes('Do you want to update the existing design') && 
            isConfirmationResponse) {
          
          if (isUserConfirmingUpdate) {
            // User confirmed they want to update existing design
            setMessages([
              ...newMessages,
              { role: 'system', content: 'Can you please describe what change you want to make to the existing design?' }
            ]);
            setIsGenerating(false);
            return;
          } else if (isUserRequestingNew) {
            // User wants a new design
            setMessages([
              ...newMessages,
              { role: 'system', content: 'This looks like a design request! Let me create that for you.' }
            ]);
            
            // Call generate UI with the original text and design style
            handleSendMessage(messages[messages.length - 2].content, [], deviceType, designStyle);
            return;
          }
        }
        
        // If the message is very short and ambiguous
        const isAmbiguousRequest = text.split(' ').length < 5 || !text.includes(' ');
        
        if (isAmbiguousRequest) {
          setMessages([
            ...newMessages,
            { role: 'system', content: 'Do you want to update the existing design or create a new design? (Reply with "update" or "new")' }
          ]);
          setIsGenerating(false);
          return;
        }
        
        // Default to treating it as a modification request if we have HTML
        // Call modify UI with the current HTML and design style
        setMessages([
          ...newMessages,
          { role: 'system', content: `Updating your ${deviceType} design...`, loading: true }
        ]);
        
        const elementDescription = "the element described";
        const response = await GeminiService.modifyShadcnUI(elementDescription, text, currentHTML, null, deviceType, designStyle);
        
        if (response.error) {
          // Handle API error from backend
          setMessages([
            ...newMessages,
            { role: 'system', content: `Sorry, I couldn't make that change. ${response.error}`, error: true }
          ]);
          return;
        }
        
        // Success - update messages and set HTML
        setMessages([
          ...newMessages,
          { role: 'system', content: `I've updated your design. You can continue to refine it or ask for other changes.` }
        ]);
        
        setCurrentHTML(response.html);
        setAnnotations([]);
        setIsGenerating(false);
        
        // Add to version history with the modification text as description
        addVersionToHistory(response.html, text);
        return;
      }
      
      // If we don't have current HTML or user explicitly requested a new design
      // Not a modification request, proceed with normal chat flow
      const response = await GeminiService.sendChatMessage(text, deviceType);
      
      if (response.type === "error") {
        // Handle API error
        setMessages([
          ...newMessages,
          { role: 'system', content: `Sorry, I couldn't process that. ${response.content}`, error: true }
        ]);
        return;
      }
      
      if (response.type === "design_query") {
        // This is a design query - handle with generateUI
        setMessages([
          ...newMessages,
          { role: 'system', content: 'This looks like a design request! Let me create that for you.' }
        ]);
        
        // Call generate UI with the same text and design style
        handleSendMessage(text, [], deviceType, designStyle);
        return;
      }
      
      // This is a text response - just display it
      setMessages([
        ...newMessages,
        { role: 'system', content: response.content }
      ]);
      
    } catch (error) {
      console.error('Chat error:', error);
      setMessages([
        ...newMessages,
        { role: 'system', content: `Sorry, something went wrong. ${error.message || 'Please try again.'}`, error: true }
      ]);
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Send message and generate UI using Gemini
  const handleSendMessage = async (
    text = userInput, 
    uploadedFiles = [], 
    deviceType = 'web', 
    designStyle = 'creative', // Default to creative if not specified
    isDeviceTypeChangeOnly = false
  ) => {
    if (!text && uploadedFiles.length === 0 && !isDeviceTypeChangeOnly) return;

    const userMessageContent = isDeviceTypeChangeOnly 
      ? `Changed device view to ${deviceType}` 
      : (text || 'Generating design from uploaded file(s)');
    
    const newMessage = { role: 'user', content: userMessageContent };
    const updatedMessages = [...messages, newMessage];
    setMessages(updatedMessages);
    setUserInput(''); // Clear input after sending

    // Show loading message immediately after user message
    const loadingMessages = [...updatedMessages, { role: 'system', content: 'Generating design...', loading: true }];
    setMessages(loadingMessages);

    if (!isDeviceTypeChangeOnly) {
      setIsGenerating(true);
      startProgressSimulation(); // Start progress simulation
      setShowCode(false); // Switch back to design view when generating
      
      // Clear annotations when generating a new design
      setAnnotations([]); 
      setSelectedDrawings([]);

      let result;
      const formData = new FormData();
      let hasFiles = false;
      if (uploadedFiles && uploadedFiles.length > 0) {
        hasFiles = true;
        uploadedFiles.forEach((file) => {
          formData.append('files', file); // Use 'files' as the key
        });
        // Append other necessary data for file upload endpoints
        formData.append('prompt', text || 'Generate UI from image');
        formData.append('deviceType', deviceType);
        // Note: designStyle is appended within the service calls if needed
      }

      // --- Logic Change: Route based on designStyle --- 
      try {
        console.log(`App.js handleSendMessage: Received designStyle = ${designStyle}`); 

        if (designStyle === 'creative') {
          console.log(`Calling generateShadcnUI (Creative) - Files: ${hasFiles}`);
          // Always use generateShadcnUI for creative style
          result = await GeminiService.generateShadcnUI(
            text || 'Generate UI from image', 
            hasFiles ? formData : null, 
            deviceType
          );
        } else { // 'standard' or 'wireframe' maps to generateUI (Plain Tailwind)
          console.log(`Calling generateUI (Standard/Wireframe) - Files: ${hasFiles}`);
          // Use generateUI for standard/wireframe style
          // We pass allowSpecializedUI: false explicitly below
          // The GeminiService.generateUI function handles constructing the body/FormData
          
          // REMOVED redundant bodyOrFormData preparation
          
           result = await GeminiService.generateUI(
             text || (hasFiles ? 'Generate UI from image' : 'Generate UI'), 
             hasFiles ? formData : null, 
             deviceType, 
             false // Pass allowSpecializedUI = false explicitly
           );
        }
        // --- End Logic Change --- 

        completeProgress(); // Complete progress simulation on success or failure

        if (result && result.error) {
          console.error("Generation Error:", result);
          let errorMessage = result.error || 'Failed to generate UI.';
          if (result.loginRequired) {
             errorMessage = "Request limit reached. Please log in to continue.";
             // Optional: redirect to login or show login prompt
             // navigate('/auth');
          }
          // Remove loading message and add error
          setMessages(prev => [
              ...prev.filter(m => !m.loading),
              { role: 'system', content: `Error: ${errorMessage}`, error: true }
          ]);
          // Don't update HTML on error
        } else if (result && result.html) {
          setCurrentHTML(result.html);
          const successMessage = { role: 'system', content: 'Design generated successfully.' };
          let modelMessage = null;
          // Check if fallback model was used
          if (result.modelUsed && result.modelUsed !== 'gemini-2.0-flash') { // Check against the primary model name
            modelMessage = { 
              role: 'system', 
              content: `(Using fallback model ${result.modelUsed} as the primary model is busy.)`, 
              isInfo: true // Optional: style this differently
            };
          }
          let usageWarningMessage = null;
          if (result.usageWarning) {
             usageWarningMessage = { role: 'system', content: `Warning: ${result.usageWarning}`, isWarning: true };
          }
          
          // Remove loading message and add success messages
          setMessages(prev => [
             ...prev.filter(m => !m.loading),
             successMessage,
             ...(modelMessage ? [modelMessage] : []),
             ...(usageWarningMessage ? [usageWarningMessage] : [])
          ]);
          // Add the successful generation to history
          addVersionToHistory(result.html, text || (hasFiles ? 'Generated from image' : 'Generated design'));
        } else {
          console.error("Invalid response structure:", result);
          // Remove loading message and add error
          setMessages(prev => [
            ...prev.filter(m => !m.loading),
            { role: 'system', content: 'Error: Received an invalid response from the server.', error: true }
          ]);
          // Don't update HTML on invalid response
        }
      } catch (error) { // Catch errors during the API call itself (e.g., network errors)
        console.error('Network or unexpected error during generation:', error);
        completeProgress();
        setMessages(prev => [...prev, { role: 'system', content: `Error: ${error.message || 'An unexpected error occurred.'}` }]);
      }
      setIsGenerating(false);
    }
    
    // Update context size after processing
    updateContextSize(updatedMessages); 
  };

  // Handle circling an element and processing it with Gemini
  const handleElementModification = async (
      userMessage, 
      uploadedFiles = [], 
      deviceType = 'web', 
      designStyle = 'creative' // Capture design style for modification
  ) => {
    const { selectionType, coordinates, selectedElements, drawings } = activeTool === 'select' || activeTool === 'draw' 
      ? designCanvasRef.current.getSelectionDetails()
      : { selectionType: null, coordinates: null, selectedElements: [], drawings: [] }; // Default if no selection tool active
    
    // If no element is visually selected or described, don't proceed
    if (!selectedElements || selectedElements.length === 0) {
      if (activeTool === 'select') {
          setMessages(prev => [...prev, { role: 'system', content: "Please select an element or area first before asking for a modification." }]);
      } else if (activeTool === 'draw') {
          setMessages(prev => [...prev, { role: 'system', content: "Please draw on the canvas or select an element before asking for a modification." }]);
      } else {
          setMessages(prev => [...prev, { role: 'system', content: "Please activate the select or draw tool and choose an element to modify." }]);
      }
      setActiveTool('select'); // Revert to select tool
      return;
    }

    setIsGenerating(true);
    startProgressSimulation();
    setShowCode(false);

    const newMessage = { role: 'user', content: userMessage };
    const updatedMessages = [...messages, newMessage];
    setMessages(updatedMessages);
    setUserInput(''); // Clear input after sending

    // Show loading message immediately after user message
    const loadingMessages = [...updatedMessages, { role: 'system', content: 'Modifying design...', loading: true }];
    setMessages(loadingMessages);

    // Construct element description from selection
    const elementDescription = JSON.stringify({
      type: activeTool === 'draw' ? 'hand-drawn' : 'selection',
      coordinates: coordinates,
      selectionType: selectionType,
      matchingElements: selectedElements.map(el => ({ // Send minimal info
         tagName: el.tagName,
         classes: el.classes,
         id: el.id,
         textContent: el.textContent.substring(0, 50), // Truncate text
         rect: el.rect
      })),
      drawings: activeTool === 'draw' ? drawings : undefined, // Include drawing data if applicable
      message: activeTool === 'draw' ? "User drew on the canvas to indicate context for modification" : "User selected these elements with the box selection tool"
    });
    
    // Prepare FormData if files are present
    let formData = null;
    let hasFiles = false;
    if (uploadedFiles && uploadedFiles.length > 0) {
      hasFiles = true;
      formData = new FormData();
      uploadedFiles.forEach((file) => {
         formData.append('files', file);
      });
      // Other fields will be added by the service function
    }
    
    // --- Logic Change: Route modification based on designStyle ---
    let result;
    try {
      if (designStyle === 'creative') {
        console.log(`Calling modifyShadcnUI (Creative) - Files: ${hasFiles}`);
        // Use modifyShadcnUI for creative style
        result = await GeminiService.modifyShadcnUI(
           elementDescription, 
           userMessage, 
           currentHTML, 
           formData, 
           deviceType
        );
      } else { // 'standard' or 'wireframe' maps to modifyUI (Plain Tailwind)
        console.log(`Calling modifyUI (Standard/Wireframe) - Files: ${hasFiles}`);
        // Use modifyUI for standard/wireframe style
        // We need to ensure allowSpecializedUI=false is handled correctly if modifyUI uses /api/generate internally, 
        // or that /api/modify inherently doesn't allow specialized UI.
        // Let's assume modifyUI corresponds to /api/modify which expects Tailwind only.
        result = await GeminiService.modifyUI(
           elementDescription, 
           userMessage, 
           currentHTML, 
           formData, 
           deviceType, 
           'standard' // Pass style for consistency, though /api/modify might ignore it
        );
      }
      // --- End Logic Change ---

      completeProgress(); // Stop progress simulation

      if (result && result.error) {
         console.error("Modification Error:", result);
         let errorMessage = result.error || 'Failed to modify UI.';
         if (result.loginRequired) {
           errorMessage = "Request limit reached. Please log in to continue.";
         }
         // Remove loading message and add error
         setMessages(prev => [
            ...prev.filter(m => !m.loading),
            { role: 'system', content: `Error: ${errorMessage}`, error: true }
         ]);
      } else if (result && result.html) {
         setCurrentHTML(result.html);
         const successMessage = { role: 'system', content: 'Design modified successfully.' };
         let modelMessage = null;
         // Check if fallback model was used
         if (result.modelUsed && result.modelUsed !== 'gemini-2.0-flash') { // Check against the primary model name
           modelMessage = { 
             role: 'system', 
             content: `(Using fallback model ${result.modelUsed} as the primary model is busy.)`, 
             isInfo: true 
           };
         }
         let usageWarningMessage = null;
         if (result.usageWarning) {
             usageWarningMessage = { role: 'system', content: `Warning: ${result.usageWarning}`, isWarning: true };
         }
         
         // Remove loading message and add success messages
         setMessages(prev => [
            ...prev.filter(m => !m.loading),
            successMessage,
            ...(modelMessage ? [modelMessage] : []),
            ...(usageWarningMessage ? [usageWarningMessage] : [])
         ]);
         // Add the modified version to history
         addVersionToHistory(result.html, `Modified: ${userMessage}`);
      } else {
         console.error("Invalid modification response structure:", result);
         // Remove loading message and add error
         setMessages(prev => [
           ...prev.filter(m => !m.loading),
           { role: 'system', content: 'Error: Received an invalid response from the server during modification.', error: true }
         ]);
      }
    } catch (error) {
      console.error('Network or unexpected error during modification:', error);
      completeProgress();
      setMessages(prev => [...prev, { role: 'system', content: `Error: ${error.message || 'An unexpected error occurred during modification.'}` }]);
    }

    setIsGenerating(false);
    setActiveTool('select'); // Revert to select tool after modification attempt
    setAnnotations([]); // Clear annotations after modification
    setSelectedDrawings([]); // Clear selected drawings
    updateContextSize(updatedMessages); // Update context size
  };

  const handleAnnotationStart = (e) => {
    if (!currentHTML || showCode || (activeTool !== 'select' && activeTool !== 'draw')) return;
    
    // Get positions directly from the adjusted event
    const x = e.clientX;
    const y = e.clientY;

    setIsDrawing(true);
    setStartPoint({ x, y });
    
    if (activeTool === 'select') {
        // If we click on a drawing, toggle its selection
        if (annotations.length > 0) {
          // Check if we clicked on any drawings
          let clickedOnDrawing = false;
          
          // Only check drawings (strokes with points) not selection rectangles
          const strokeDrawings = annotations.filter(ann => ann.points);
          
          for (let i = 0; i < strokeDrawings.length; i++) {
            const drawing = strokeDrawings[i];
            // Simplified bounding box check for the drawing
            const drawingBounds = getDrawingBounds(drawing);
            
            if (
              x >= drawingBounds.minX - 10 && 
              x <= drawingBounds.maxX + 10 && 
              y >= drawingBounds.minY - 10 && 
              y <= drawingBounds.maxY + 10
            ) {
              // Toggle selection of this drawing
              clickedOnDrawing = true;
              if (selectedDrawings.includes(drawing.id)) {
                setSelectedDrawings(selectedDrawings.filter(id => id !== drawing.id));
              } else {
                setSelectedDrawings([...selectedDrawings, drawing.id]);
              }
              break;
            }
          }
          
          // If not clicking on a drawing, start a new selection box and clear selections
          if (!clickedOnDrawing) {
            setSelectedDrawings([]);
            setAnnotations([]);
          } else {
            // We handled a drawing selection, so don't start a new box
            setIsDrawing(false);
            return;
          }
        } else {
          // No existing annotations, just clear selections
          setSelectedDrawings([]);
        }
    } else if (activeTool === 'draw') {
        // Start a new stroke with the first point
        const newStroke = {
            id: Date.now(), // Unique ID for the stroke
            points: [{ x, y }]
        };
        setAnnotations([...annotations, newStroke]);
    }
  };
  
  // Helper function to get bounds of a drawing
  const getDrawingBounds = (drawing) => {
    if (!drawing.points || drawing.points.length === 0) {
      return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    }
    
    let minX = drawing.points[0].x;
    let minY = drawing.points[0].y;
    let maxX = drawing.points[0].x;
    let maxY = drawing.points[0].y;
    
    drawing.points.forEach(point => {
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    });
    
    return { minX, minY, maxX, maxY };
  };
  
  const handleAnnotationChange = (e) => {
    if (!isDrawing || !currentHTML || showCode || (activeTool !== 'select' && activeTool !== 'draw')) return;
    
    // Get positions directly from the adjusted event
    const currentX = e.clientX;
    const currentY = e.clientY;

    if (activeTool === 'select') {
        setAnnotations([{
          x: Math.min(startPoint.x, currentX),
          y: Math.min(startPoint.y, currentY),
          width: Math.abs(currentX - startPoint.x),
          height: Math.abs(currentY - startPoint.y)
        }]);
    } else if (activeTool === 'draw') {
        // Add new point to the current stroke
        if (annotations.length === 0) return;
        
        const currentStrokes = [...annotations];
        const currentStroke = currentStrokes[currentStrokes.length - 1];
        
        // Only add points if they're a minimum distance from the last point
        // to avoid too many points which can slow down performance
        const lastPoint = currentStroke.points[currentStroke.points.length - 1];
        const dx = currentX - lastPoint.x;
        const dy = currentY - lastPoint.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance > 2) { // Minimum 2px distance between points
            currentStroke.points.push({ x: currentX, y: currentY });
            setAnnotations(currentStrokes);
        }
    }
  };
  
  const handleAnnotationComplete = () => {
    if (isDrawing) {
      setIsDrawing(false);
      if (activeTool === 'select') {
          if (annotations.length > 0 && annotations[0].width > 5 && annotations[0].height > 5) {
            console.log('DEBUG: handleAnnotationComplete - Final annotation:', JSON.stringify(annotations[0]));
            setMessages([
              ...messages,
              { role: 'system', content: 'What changes would you like to make to this element?' }
            ]);
          } else {
            console.log('DEBUG: handleAnnotationComplete - Annotation too small or invalid, clearing.');
            setAnnotations([]);
          }
      } else if (activeTool === 'draw') {
          // Finalize the drawn path/stroke - nothing to do as strokes are already in annotations
          // But we could optimize the path here if needed (e.g., simplify)
          console.log('DEBUG: handleAnnotationComplete - Drawing complete.');
      }
    }
  };
  
  const handleAnnotationCancel = () => {
      if (isDrawing) {
          setIsDrawing(false);
          if (activeTool === 'select') {
            setAnnotations([]);
          } else if (activeTool === 'draw') {
            // Remove the last stroke that was being drawn
            if (annotations.length > 0) {
              const newAnnotations = [...annotations];
              newAnnotations.pop(); // Remove the incomplete stroke
              setAnnotations(newAnnotations);
            }
          }
      }
  };

  // Wrap in useCallback to fix dependency warning
  const handleClearAnnotations = useCallback(() => {
    // Clear all annotations/drawings
    setAnnotations([]);
    setSelectedDrawings([]);
  }, []);

  const handleZoomIn = () => {
      designCanvasRef.current?.zoomIn();
  };

  const handleZoomOut = () => {
      designCanvasRef.current?.zoomOut();
  };

  // Wrap in useCallback to fix dependency warning
  const handleSetTool = useCallback((tool) => {
    // If we're switching from draw to another tool, ask if the user wants to keep their drawings
    if (activeTool === 'draw' && tool !== 'draw' && annotations.length > 0) {
      const keepDrawings = window.confirm('Do you want to keep your drawings? Click OK to keep them, or Cancel to clear them.');
      if (!keepDrawings) {
        setAnnotations([]);
        setSelectedDrawings([]);
      }
    } 
    // If switching to select tool from anything else, clear annotations
    else if (tool === 'select') {
      setAnnotations([]);
      setSelectedDrawings([]);
    }
    
    setActiveTool(tool);
  }, [activeTool, annotations.length]);

  const exportHTML = () => {
    // Track event with Google Analytics only if consent is given
    if (analyticsConsentGiven && typeof window.gtag === 'function') {
      window.gtag('event', 'export_click', {
        'event_category': 'engagement',
        'event_label': 'Export HTML Button Click'
      });
    }

    // Create a complete HTML document with Tailwind CSS and shadcn/ui variables
    const fullHTML = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>UIForge Design</title>
  <!-- Include Tailwind CSS CDN -->
  <script src="https://cdn.tailwindcss.com"></script>
  <style>
    /* shadcn/ui CSS variables */
    :root {
      --background: 0 0% 100%;
      --foreground: 222.2 84% 4.9%;
      --card: 0 0% 100%;
      --card-foreground: 222.2 84% 4.9%;
      --popover: 0 0% 100%;
      --popover-foreground: 222.2 84% 4.9%;
      --primary: 221.2 83.2% 53.3%;
      --primary-foreground: 210 40% 98%;
      --secondary: 210 40% 96.1%;
      --secondary-foreground: 222.2 47.4% 11.2%;
      --muted: 210 40% 96.1%;
      --muted-foreground: 215.4 16.3% 46.9%;
      --accent: 210 40% 96.1%;
      --accent-foreground: 222.2 47.4% 11.2%;
      --destructive: 0 84.2% 60.2%;
      --destructive-foreground: 210 40% 98%;
      --border: 214.3 31.8% 91.4%;
      --input: 214.3 31.8% 91.4%;
      --ring: 221.2 83.2% 53.3%;
      --radius: 0.5rem;
    }

    .dark {
      --background: 222.2 84% 4.9%;
      --foreground: 210 40% 98%;
      --card: 222.2 84% 4.9%;
      --card-foreground: 210 40% 98%;
      --popover: 222.2 84% 4.9%;
      --popover-foreground: 210 40% 98%;
      --primary: 217.2 91.2% 59.8%;
      --primary-foreground: 222.2 47.4% 11.2%;
      --secondary: 217.2 32.6% 17.5%;
      --secondary-foreground: 210 40% 98%;
      --muted: 217.2 32.6% 17.5%;
      --muted-foreground: 215 20.2% 65.1%;
      --accent: 217.2 32.6% 17.5%;
      --accent-foreground: 210 40% 98%;
      --destructive: 0 62.8% 30.6%;
      --destructive-foreground: 210 40% 98%;
      --border: 217.2 32.6% 17.5%;
      --input: 217.2 32.6% 17.5%;
      --ring: 224.3 76.3% 48%;
    }
  </style>
</head>
<body class="min-h-screen bg-background font-sans antialiased">
${currentHTML}
</body>
</html>`;

    const blob = new Blob([fullHTML], { type: 'text/html' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'design.html';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    console.log('Exporting HTML with Tailwind CSS and shadcn/ui variables included');
  };
  
  const resetDesign = () => {
    if (window.confirm('Are you sure you want to reset your design?')) {
      setCurrentHTML('');
      setAnnotations([]);
      setShowCode(false);
      setMessages([
        { role: 'system', content: 'What should we forge?' }
      ]);
      setActiveTool('select');
      
      // Clear version history
      clearVersionHistory();
    }
  };

  // Show welcome message based on backend status
  useEffect(() => {
    const userName = user ? user.name : 'Guest';
    
    // Check backend health on load
    GeminiService.checkBackendHealth().then(isReady => {
      setIsBackendReady(isReady);
      
      // Set initial message based on backend status
      const initialMessage = isReady 
        ? `What should we forge?`
        : `Welcome, ${userName}! Note: The AI service is currently unavailable. Please try again soon.`;
          
      setMessages([
        { role: 'system', content: initialMessage }
      ]);
    });
  }, [user]);
  
  // Clean up progress interval on unmount
  useEffect(() => {
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, []);

  // Update context size whenever messages or HTML changes
  useEffect(() => {
    updateContextSize(messages);
  }, [messages, updateContextSize]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only apply shortcuts when not typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      switch(e.key.toLowerCase()) {
        case 's':
          handleSetTool('select');
          break;
        case 'p':
          handleSetTool('pan');
          break;
        case 'd':
          handleSetTool('draw');
          break;
        case 'escape':
          if (annotations.length > 0) {
            handleClearAnnotations();
          }
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [annotations.length, handleSetTool, handleClearAnnotations]);

  // Show message to convert selected drawings to UI elements
  useEffect(() => {
    if (selectedDrawings.length > 0) {
      setMessages(prevMessages => {
        const lastMessage = prevMessages[prevMessages.length - 1];
        if (lastMessage.role === 'system' && lastMessage.content.includes('selected your drawing')) {
          // Don't add duplicate message
          return prevMessages;
        }
        return [
          ...prevMessages,
          { 
            role: 'system', 
            content: `I've selected your drawing. Tell me what UI element you'd like me to create from it.`
          }
        ];
      });
    }
  }, [selectedDrawings]);

  // --- JSX Structure ---
  return (
    <>
      {/* Default Helmet for the app */}
      <Helmet
        defaultTitle="UIForge - AI UI Builder"
        titleTemplate="%s | UIForge"
      >
        <meta name="description" content="Build user interfaces instantly using AI. Describe your UI, and UIForge generates the design and code." />
        {/* Add other default tags like Open Graph here if needed */}
        <meta property="og:title" content="UIForge - AI UI Builder" />
        <meta property="og:description" content="Build user interfaces instantly using AI. Describe your UI, and UIForge generates the design and code." />
        {/* <meta property="og:image" content="URL_TO_YOUR_PREVIEW_IMAGE" /> */}
        {/* <meta property="og:url" content="YOUR_WEBSITE_URL" /> */}
        <meta name="twitter:card" content="summary_large_image" />
        {/* Add more tags as needed */}
      </Helmet>

      <div className="flex flex-col h-screen bg-gray-50 dark:bg-dark-bg-primary">
        <Toolbar 
          onExportClick={exportHTML} 
          isGenerating={isGenerating}
          onReset={resetDesign}
          showCode={showCode}
          onToggleCode={() => setShowCode(!showCode)}
          versionHistory={versionHistory}
          onSelectVersion={loadVersionFromHistory}
        />
        {showContextWarning && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-yellow-800 text-sm flex items-center justify-between">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Context window is getting full. Some design history may be lost in future modifications.
            </div>
            <button 
              onClick={resetDesign} 
              className="text-yellow-700 hover:text-yellow-900 font-medium ml-4"
            >
              Reset Design
            </button>
          </div>
        )}
        <div className="flex-1 flex overflow-hidden bg-gray-50 dark:bg-dark-bg-primary">
          <div className="flex-1 flex flex-col md:flex-row overflow-hidden bg-gray-50 dark:bg-dark-bg-primary">
            <div className="flex-1 overflow-auto relative bg-gray-50 dark:bg-dark-bg-primary">
              {progress < 100 && isGenerating && (
                <div className="absolute inset-x-0 top-0 z-50">
                  <ProgressBar percent={progress} timeRemaining={timeRemaining} />
                </div>
              )}
              
              {showCode ? (
                <CodePreview html={currentHTML} />
              ) : (
                <div className="h-full relative bg-gray-50 dark:bg-dark-bg-primary">
                  <DesignCanvas 
                    html={currentHTML}
                    ref={designCanvasRef}
                    onAnnotationStart={handleAnnotationStart}
                    onAnnotationChange={handleAnnotationChange}
                    onAnnotationComplete={handleAnnotationComplete}
                    deviceType={currentDeviceType}
                    annotations={annotations}
                    isDrawing={isDrawing}
                    startPoint={startPoint}
                    activeTool={activeTool}
                    selectedDrawings={selectedDrawings}
                  />
                </div>
              )}
              
              <CanvasToolbar 
                onChangeView={() => setShowCode(!showCode)} 
                viewingCode={showCode}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                onAnnotationCancel={handleAnnotationCancel}
                onSetTool={handleSetTool}
                activeTool={activeTool}
                onClearAnnotations={handleClearAnnotations}
                hasAnnotations={annotations.length > 0}
              />
            </div>
            <ChatPanel 
              messages={messages} 
              userInput={userInput} 
              onUserInput={setUserInput} 
              onSendMessage={handleSendMessage}
              isGenerating={isGenerating}
              onSubmitModification={handleElementModification}
              onChatMessage={handleChatMessage}
              analyticsConsentGiven={analyticsConsentGiven}
              onDeviceTypeChange={handleDeviceTypeChange}
              currentDeviceType={currentDeviceType}
            />
          </div>
        </div>
      </div>
      <CookieConsent
        location="bottom"
        buttonText="Accept"
        declineButtonText="Decline"
        cookieName="CookieConsent"
        style={{ background: "#2B373B", zIndex: 100 }}
        buttonStyle={{ color: "#4e503b", fontSize: "13px" }}
        declineButtonStyle={{ fontSize: "13px" }}
        expires={150}
        enableDeclineButton
        onAccept={() => {
          // Update gtag consent state first
          if (typeof window.gtag === 'function') {
            window.gtag('consent', 'update', {
              'ad_storage': 'granted',
              'ad_user_data': 'granted',
              'ad_personalization': 'granted',
              'analytics_storage': 'granted'
            });
            console.log("Gtag consent updated to granted.");
          } else {
            console.log("window.gtag not defined when trying to update consent.");
          }
          // Then update React state and load GA
          setAnalyticsConsentGiven(true);
          console.log("Analytics consent accepted. Loading GA...");
          loadGoogleAnalytics(); // Load GA script on accept
        }}
        onDecline={() => {
          setAnalyticsConsentGiven(false);
          console.log("Analytics consent declined.");
        }}
      >
        This website uses cookies for analytics purposes. See our{" "}
        <a href="/privacy-policy" style={{ color: "#a1c9f7", textDecoration: "underline" }}>privacy policy</a> for more details.
      </CookieConsent>
    </>
  );
}

// Main App component with routing
function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public Routes */}
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        
        {/* Main Application Layout (now public) */}
        {/* Remove ProtectedRoute wrapper */}
        <Route path="/" element={<MainLayout />}> 
          <Route index element={<AppUI />} />
          <Route path="help" element={<HelpPage />} />
          {/* Profile route still needs protection */}
          <Route 
            path="profile" 
            element={ <ProtectedRoute><ProfilePage /></ProtectedRoute> } 
          />
        </Route>
        
        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} /> 
      </Routes>
    </AuthProvider>
  );
}

export default App; 